import React, { useState } from 'react'
import { ScheduleCard as StyledScheduleCard, TabContainer, Tab } from './schedule-components/styles'
import { convertDailyToCron, convertWeeklyToCron } from './schedule-components/cronUtils'
import { HourlySchedule } from './schedule-components/HourlySchedule'
import { DailySchedule } from './schedule-components/DailySchedule'
import { WeeklySchedule } from './schedule-components/WeeklySchedule'
import { CustomSchedule } from './schedule-components/CustomSchedule'

export const TabIds = {
  hourly: 'hourly',
  daily: 'daily',
  weekly: 'weekly',
  custom: 'custom',
} as const

export type ScheduleType = keyof typeof TabIds

export const DEFAULT_SCHEDULES = {
  custom: '0 9 * * *',
  hourly: '00 09 * * *',
  daily: {
    type: 'weekdays' as const,
    days: 1,
    startTime: '09:00',
  },
  weekly: {
    days: [1] as number[],
    startTime: '09:00',
  },
} as const

interface ScheduleCardProps {
  onScheduleTypeChange?: (type: ScheduleType) => void
  onCronExpressionChange?: (expression: string) => void
  onError?: (error: string | null) => void
  defaultType?: ScheduleType
  initialCronExpression?: string
  disabled?: boolean
}

export const ScheduleCard: React.FC<ScheduleCardProps> = ({
  onScheduleTypeChange,
  onCronExpressionChange,
  onError,
  defaultType = 'hourly',
  initialCronExpression,
  disabled = false,
}) => {
  const [selectedTab, setSelectedTab] = useState<ScheduleType>(defaultType)
  const [customScheduleValue, setCustomScheduleValue] = useState<string>(
    initialCronExpression || DEFAULT_SCHEDULES.custom
  )

  const handleTabChange = (type: ScheduleType) => {
    setSelectedTab(type)
    onScheduleTypeChange?.(type)
    onError?.(null)

    switch (type) {
      case 'custom':
        onCronExpressionChange?.(customScheduleValue)
        break
      case 'hourly':
        handleHourlyScheduleChange(DEFAULT_SCHEDULES.hourly)
        break
      case 'daily':
        handleDailyScheduleChange(DEFAULT_SCHEDULES.daily)
        break
      case 'weekly':
        handleWeeklyScheduleChange(DEFAULT_SCHEDULES.weekly)
        break
    }
  }

  const handleHourlyScheduleChange = (expression: string) => {
    onCronExpressionChange?.(expression)
  }

  const handleDailyScheduleChange = (schedule: {
    type: 'every_n_days' | 'weekdays'
    days?: number
    startTime: string
  }) => {
    const { cron } = convertDailyToCron(schedule)
    onCronExpressionChange?.(cron)
  }

  const handleWeeklyScheduleChange = (schedule: { days: number[]; startTime: string }) => {
    const { cron } = convertWeeklyToCron(schedule)
    onCronExpressionChange?.(cron)
  }

  const handleCustomScheduleChange = (expression: string) => {
    setCustomScheduleValue(expression)
    onCronExpressionChange?.(expression)
  }

  return (
    <StyledScheduleCard disabled={disabled}>
      <TabContainer>
        <Tab
          isActive={selectedTab === 'custom'}
          onClick={() => handleTabChange('custom')}
          disabled={disabled}
        >
          Custom
        </Tab>
        <Tab
          isActive={selectedTab === 'hourly'}
          onClick={() => handleTabChange('hourly')}
          disabled={disabled}
        >
          Hourly
        </Tab>
        <Tab
          isActive={selectedTab === 'daily'}
          onClick={() => handleTabChange('daily')}
          disabled={disabled}
        >
          Daily
        </Tab>
        <Tab
          isActive={selectedTab === 'weekly'}
          onClick={() => handleTabChange('weekly')}
          disabled={disabled}
        >
          Weekly
        </Tab>
      </TabContainer>

      <div>
        {selectedTab === 'custom' && (
          <CustomSchedule
            onScheduleChange={handleCustomScheduleChange}
            onError={onError}
            defaultValue={customScheduleValue}
          />
        )}
        {selectedTab === 'hourly' && (
          <HourlySchedule
            onScheduleChange={handleHourlyScheduleChange}
            defaultSchedule={DEFAULT_SCHEDULES.hourly}
          />
        )}
        {selectedTab === 'daily' && (
          <DailySchedule
            onScheduleChange={handleDailyScheduleChange}
            defaultSchedule={DEFAULT_SCHEDULES.daily}
          />
        )}
        {selectedTab === 'weekly' && (
          <WeeklySchedule
            onScheduleChange={handleWeeklyScheduleChange}
            defaultSchedule={DEFAULT_SCHEDULES.weekly}
          />
        )}
      </div>
    </StyledScheduleCard>
  )
}
